import React, { useState, useEffect } from 'react';
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  InputAdornment,
  CardActions,
  TableSortLabel,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
  Tooltip,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import { useAuthContext } from '../../../../hooks/useAuthContext';
import axios from '../../../../services/ApiService';
import { paths } from '../../../../utils/paths';
import LoadingSpinner from '../../../support/LoadingSpinner';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { ProgramSubscription, ProgramData } from '../../../types/ProgramTypes';
import { BVTConstants } from '../../../../utils/BVTConstants';

type Order = 'asc' | 'desc';
type OrderBy = 'startDate' | 'endDate' | 'programName' | 'companyName' | 'amount';

function CompanySubscriptions() {
  const [loading, setLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState<ProgramSubscription[]>([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState<ProgramSubscription[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<OrderBy>('startDate');
  const [includeInactive, setIncludeInactive] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubscriptions();
  }, [includeInactive]);

  useEffect(() => {
    filterAndSortSubscriptions();
  }, [subscriptions, searchTerm, order, orderBy]);

  const fetchSubscriptions = async () => {
    setSubscriptions([]);
    setLoading(true);
    try {
      const response = await axios.post('/getAllProgramSubscriptions', includeInactive);
      if (Array.isArray(response.data)) {
        setSubscriptions(response.data);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      //toast.error('Failed to fetch subscriptions');
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleIncludeInactiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIncludeInactive(e.target.checked);
  };

  const filterAndSortSubscriptions = () => {
    // Ensure subscriptions is an array before filtering
    if (!Array.isArray(subscriptions)) {
      setFilteredSubscriptions([]);
      return;
    }

    let filtered = subscriptions.filter(subscription =>
      subscription.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subscription.programName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    filtered.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (orderBy) {
        case 'startDate':
          aValue = new Date(a.startDate).getTime();
          bValue = new Date(b.startDate).getTime();
          break;
        case 'endDate':
          aValue = new Date(a.endDate).getTime();
          bValue = new Date(b.endDate).getTime();
          break;
        case 'programName':
          aValue = a.companyName;
          bValue = b.companyName;
          break;
        case 'amount':
          aValue = a.amount;
          bValue = b.amount;
          break;
        default:
          aValue = a.companyName;
          bValue = b.companyName;
      }

      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredSubscriptions(filtered);
  };

  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'suspended':
        return 'error';
      case 'pending':
      case 'overdue':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    if(dateString === 'NA'){
      return 'NA';
    }
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <>
      {loading ? (
        <LoadingSpinner loading={loading} />
      ) : (
        <Card>
          <CardHeader 
            title="Program Subscriptions"
          />
          <Divider />
          <CardContent>
            <Stack spacing={3}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <TextField
                  fullWidth
                  placeholder="Search by program or company name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <Tooltip title="Include Completed Subscriptions" arrow>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeInactive}
                        onChange={handleIncludeInactiveChange}
                        color="primary"
                      />
                    }
                    label="Completed Subscriptions"
                    sx={{ minWidth: 350, marginLeft: 1 }}
                  />
                </Tooltip>
                </Stack>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'programName'}
                          direction={orderBy === 'programName' ? order : 'asc'}
                          onClick={() => handleRequestSort('programName')}
                        >
                          Program Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'companyName'}
                          direction={orderBy === 'companyName' ? order : 'asc'}
                          onClick={() => handleRequestSort('companyName')}
                        >
                          Company Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'amount'}
                          direction={orderBy === 'amount' ? order : 'asc'}
                          onClick={() => handleRequestSort('amount')}
                        >
                          Amount
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Frequency</TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'startDate'}
                          direction={orderBy === 'startDate' ? order : 'asc'}
                          onClick={() => handleRequestSort('startDate')}
                        >
                          Start Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'endDate'}
                          direction={orderBy === 'endDate' ? order : 'asc'}
                          onClick={() => handleRequestSort('endDate')}
                        >
                          End Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSubscriptions.map((subscription) => (
                      <TableRow 
                        key={subscription.subId}
                        onClick={() => navigate(paths.agent.companySubscriptionDetails, { state: { subscriptionId: subscription.subId } })}
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                      >
                        <TableCell>{subscription.programName}</TableCell>
                        <TableCell>{subscription.companyName}</TableCell>
                        <TableCell>
                          {subscription.amount.toFixed(2)}
                        </TableCell>
                        <TableCell>{subscription.frequency}</TableCell>
                        <TableCell>{formatDate(subscription.startDate)}</TableCell>
                        <TableCell>{formatDate(subscription.endDate || 'NA')}</TableCell>
                        <TableCell>
                          <Chip
                            label={subscription.status}
                            color={getStatusColor(subscription.status) as any}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredSubscriptions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No subscriptions found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </CardContent>
        </Card>
      )}
    </>
  );
}

export default CompanySubscriptions;